import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
// Auto-refresh interval — how often the module polls the CMS so admin
// uploads land on already-open user tabs without a manual reload.
const POLL_INTERVAL_MS = 45 * 1000;

// Module-level cache of all CMS overrides — fetched once, shared across all
// `useSiteAsset` consumers. Refreshes on manual `refreshSiteAssets()` call OR
// automatically every POLL_INTERVAL_MS while the tab is visible.
let _cache = null;
let _pending = null;
let _pollTimer = null;
let _lastFetchAt = 0;
const _listeners = new Set();

async function _fetchAll(force = false) {
    if (_pending) return _pending;
    // Cache-bust query string so we always get the latest overrides.
    const bust = force ? `?t=${Date.now()}` : "";
    _pending = axios
        .get(`${BACKEND}/api/public/site-assets${bust}`)
        .then((r) => {
            _cache = r.data?.assets || {};
            _lastFetchAt = Date.now();
            _pending = null;
            _listeners.forEach((cb) => cb(_cache));
            return _cache;
        })
        .catch(() => {
            _cache = _cache || {};
            _pending = null;
            return _cache;
        });
    return _pending;
}

export function refreshSiteAssets() {
    _cache = null;
    return _fetchAll(true);
}

// Kick off periodic polling — set up once per tab. The interval pauses when
// the tab is hidden and resumes on focus, so we don't hammer the server for
// background tabs but still catch admin uploads promptly on active pages.
function _startPolling() {
    if (_pollTimer || typeof window === "undefined") return;
    const tick = () => {
        if (document.visibilityState === "visible" && Date.now() - _lastFetchAt >= POLL_INTERVAL_MS - 1000) {
            _fetchAll(true);
        }
    };
    _pollTimer = setInterval(tick, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible" && Date.now() - _lastFetchAt >= POLL_INTERVAL_MS) {
            _fetchAll(true);
        }
    });
}

function _fullUrl(u) {
    if (!u) return null;
    if (u.startsWith("http")) return u;
    return `${BACKEND}${u}`;
}

/**
 * Returns the CMS-overridden image URL (+ alt/caption) for a given slot key,
 * or the provided fallback URL if no override exists.
 *
 *   const { url, alt, caption } = useSiteAsset("illustration.governance", DEFAULT_URL);
 */
export function useSiteAsset(slotKey, fallbackUrl = null) {
    const [state, setState] = useState(() => {
        if (_cache && _cache[slotKey]) {
            const hit = _cache[slotKey];
            return {
                url: _fullUrl(hit.image_url) || fallbackUrl,
                alt: hit.alt || null,
                caption: hit.caption || null,
                title: hit.title || null,
                description: hit.description || null,
                video_url: hit.video_url || null,
                focal: hit.focal || null,
                fit: hit.fit || null,
                fromCms: true,
            };
        }
        return { url: fallbackUrl, alt: null, caption: null, title: null, description: null, video_url: null, focal: null, fit: null, fromCms: false };
    });

    useEffect(() => {
        let cancelled = false;
        _startPolling();
        const apply = (cache) => {
            if (cancelled) return;
            const hit = cache[slotKey];
            if (hit && (hit.image_url || hit.title || hit.description || hit.video_url)) {
                setState({
                    url: _fullUrl(hit.image_url) || fallbackUrl,
                    alt: hit.alt || null,
                    caption: hit.caption || null,
                    title: hit.title || null,
                    description: hit.description || null,
                    video_url: hit.video_url || null,
                    focal: hit.focal || null,
                    fit: hit.fit || null,
                    fromCms: true,
                });
            } else {
                setState({ url: fallbackUrl, alt: null, caption: null, title: null, description: null, video_url: null, focal: null, fit: null, fromCms: false });
            }
        };
        if (_cache) {
            apply(_cache);
        } else {
            _fetchAll().then(apply);
        }
        _listeners.add(apply);
        return () => {
            cancelled = true;
            _listeners.delete(apply);
        };
    }, [slotKey, fallbackUrl]);

    return state;
}
